import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, ExternalLink, Loader2, Pause, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { api } from '../api/client.js';
import { Button } from '../components/Button.jsx';
import { Card, CardHeader } from '../components/Card.jsx';
import { Banner } from '../components/Banner.jsx';
import { Pill } from '../components/Pill.jsx';
import { Input } from '../components/Field.jsx';
import { Table, THead, TBody, TR, TH, TD, Empty } from '../components/Table.jsx';
import { DnsPreview, DnsOutcome, useDnsPreview } from '../components/DnsPreview.jsx';
import { VisitsLine, VisitsModal } from '../components/StaticVisits.jsx';

/**
 * Static sites: hand-added list beside the WordPress table. Deploy stays with
 * each site's own script. Where vps1 says it manages the site (its vhost on the
 * panel's box serves the row's folder) the row gets Pause / Resume / Delete;
 * elsewhere only Remove, which drops the panel row and never touches a server.
 * Independent of GET /api/sites, so it shows even when VPS 1 is down.
 */
export function StaticSites() {
  const [state, setState] = useState({ status: 'loading' });
  const [form, setForm] = useState(null); // null | { editing: domain|null, values }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [acting, setActing] = useState(null); // domain whose pause/resume is running
  const [deleting, setDeleting] = useState(null); // site row in the Delete modal
  const [visitsOf, setVisitsOf] = useState(null); // domain whose visitors modal is open
  // DNS auto on add: A @ + www → the row's server (IP in its label, else this server).
  const [setDns, setSetDns] = useState(true);
  const [dnsNonce, setDnsNonce] = useState(0);
  const [dnsResult, setDnsResult] = useState(null);
  const addDomain = !form || form.editing ? '' : form.values.domain.trim().toLowerCase();
  const dns = useDnsPreview(/^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(addDomain)
    ? { path: '/api/sites/dns/preview', body: { domain: addDomain, kind: 'static', server_label: form.values.server_label }, nonce: dnsNonce, delay: 700 }
    : null);

  const load = useCallback(() => {
    api('/api/sites/static')
      .then((data) => setState({ status: 'ok', sites: data.sites ?? [] }))
      .catch((err) => setState({ status: 'error', message: err.message }));
  }, []);
  useEffect(load, [load]);

  const open = (site) =>
    setForm({
      editing: site?.domain ?? null,
      values: {
        domain: site?.domain ?? '',
        server_label: site?.server_label ?? '',
        docroot: site?.docroot ?? '',
        notes: site?.notes ?? '',
        visit_marker: site?.visit_marker ?? '',
      },
    });

  const set = (k) => (e) => setForm((f) => ({ ...f, values: { ...f.values, [k]: e.target.value } }));

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { domain, ...rest } = form.values;
      if (form.editing) {
        await api(`/api/sites/static/${encodeURIComponent(form.editing)}`, { method: 'PATCH', body: rest });
      } else {
        await api('/api/sites/static', { method: 'POST', body: { domain, ...rest } });
        setDnsResult(null);
        if (setDns && dns.preview?.can_apply) {
          try {
            const r = await api('/api/sites/dns/apply', {
              method: 'POST',
              body: { domain: addDomain, kind: 'static', server_label: rest.server_label, fingerprint: dns.preview.fingerprint },
            });
            setDnsResult({ ...r, domain: addDomain });
          } catch (err) {
            setDnsResult({ ok: false, domain: addDomain, detail: err.status === 409 ? 'the DNS changed since the preview — nothing written; set it from DNS manager' : (err.body?.error?.detail ?? err.body?.detail ?? err.message), mismatches: err.body?.mismatches });
          }
        }
      }
      setForm(null);
      setDnsNonce((n) => n + 1);
      load();
    } catch (err) {
      setError(
        err.message === 'duplicate_domain' ? 'That domain is already in the list.'
          : err.message === 'invalid_domain' ? 'Not a valid domain name.'
            : err.message === 'invalid_visit_marker' ? 'Visit marker must be a path starting with / (no spaces).'
            : err.message,
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove(site) {
    if (!window.confirm(`Remove ${site.domain} from the panel list? The server is not touched.`)) return;
    setError(null);
    try {
      await api(`/api/sites/static/${encodeURIComponent(site.domain)}`, { method: 'DELETE' });
      load();
    } catch (err) {
      setError(err.status === 403 ? 'You do not have permission to remove sites.' : err.message);
    }
  }

  async function toggle(site, action) {
    if (action === 'pause' && !window.confirm(
      `Pause ${site.domain}? Visitors get a 503 "Site temporarily unavailable" page until you resume. Files are not touched.`,
    )) return;
    setError(null);
    setActing(site.domain);
    try {
      await api(`/api/sites/static/${encodeURIComponent(site.domain)}/${action}`, { method: 'POST' });
      load();
    } catch (err) {
      setError(err.status === 403 ? 'You do not have permission to pause or resume sites.'
        : `${err.message}${err.body?.detail ? ` — ${err.body.detail}` : ''}`);
    } finally {
      setActing(null);
    }
  }

  const sites = state.sites ?? [];

  return (
    <Card className="mt-5">
      <CardHeader
        title="Static sites"
        chip={state.status === 'ok' ? String(sites.length) : undefined}
        description="Added by hand. Deploy stays with each site's own script; the panel checks status, and can pause or delete the ones its own server serves."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => open(null)}>
            Static site
          </Button>
        }
      />

      {error && <Banner kind="error" title="Action failed" className="m-4">{error}</Banner>}
      {dnsResult && (
        <div className="m-4"><span className="text-xs font-medium text-t1">{dnsResult.domain}: </span><DnsOutcome dns={dnsResult} /></div>
      )}
      {state.status === 'error' && (
        <Banner kind="error" title="Couldn't load static sites" className="m-4">{state.message}</Banner>
      )}

      {form && (
        <form onSubmit={save} className="grid gap-3 border-b border-bd p-4 sm:grid-cols-2">
          <Input label="Domain" mono required disabled={!!form.editing} value={form.values.domain}
            onChange={set('domain')} placeholder="albajiacademy.webance.net" />
          <Input label="Server" value={form.values.server_label} onChange={set('server_label')}
            placeholder="Contabo 167.86.109.176" />
          <Input label="Folder" mono value={form.values.docroot} onChange={set('docroot')}
            placeholder="/var/www/albajiacademy" />
          <Input label="Notes" value={form.values.notes} onChange={set('notes')} />
          <Input label="Visit marker (optional)" mono value={form.values.visit_marker} onChange={set('visit_marker')}
            placeholder="/data/site.js"
            hint="A path every real page view loads. Empty = count browsers opening / (less exact)." />
          {!form.editing && (dns.loading || dns.preview || dns.error) && (
            <div className="sm:col-span-2 rounded-md border border-bd bg-s-2 p-3 space-y-2">
              <DnsPreview {...dns} title="DNS for this site" />
              {dns.preview?.can_apply && (
                <label className="flex items-center gap-2 text-xs text-t1">
                  <input type="checkbox" checked={setDns} onChange={(e) => setSetDns(e.target.checked)} />
                  Set these DNS records when adding (ADD / EDIT rows only)
                </label>
              )}
            </div>
          )}
          <div className="flex gap-2 sm:col-span-2">
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Saving…' : form.editing ? 'Save' : 'Add'}
            </Button>
            <Button onClick={() => setForm(null)}>Cancel</Button>
          </div>
        </form>
      )}

      {state.status === 'loading' && (
        <div className="flex items-center justify-center gap-2 p-8 text-[13.5px] text-t3">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking static sites…
        </div>
      )}
      {state.status === 'ok' && sites.length === 0 && (
        <div className="p-8 text-center text-[13px] text-t3">No static sites added yet.</div>
      )}
      {state.status === 'ok' && sites.length > 0 && (
        <Table>
          <THead>
            <TR hover={false}>
              <TH>Domain</TH>
              <TH>Server</TH>
              <TH>Folder</TH>
              <TH>Status</TH>
              <TH>Certificate</TH>
              <TH>Notes</TH>
              <TH className="w-40" />
            </TR>
          </THead>
          <TBody>
            {sites.map((s) => {
              const managed = s.server?.managed === true;
              return (
                <TR key={s.domain}>
                  <TD name>
                    <a href={`https://${s.domain}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 hover:text-blue hover:underline">
                      {s.domain}
                      <ExternalLink className="w-3 h-3 text-ic-light" />
                    </a>
                    <div className="mt-0.5"><VisitsLine visits={s.visits} onClick={() => setVisitsOf(s.domain)} /></div>
                  </TD>
                  <TD>{s.server_label || <Empty />}</TD>
                  <TD mono>{s.docroot || <Empty />}</TD>
                  <TD className="whitespace-nowrap">
                    <StatusChip status={s.status} />
                    {s.server?.paused && <Pill state="suspended" className="ml-1.5">Paused</Pill>}
                  </TD>
                  <TD className="whitespace-nowrap"><Cert days={s.status?.cert_days_left} /></TD>
                  <TD>{s.notes || <Empty />}</TD>
                  <TD className="text-right whitespace-nowrap">
                    {!managed && <Elsewhere server={s.server} />}
                    {managed && (acting === s.domain
                      ? <Loader2 className="inline w-4 h-4 m-1 animate-spin text-ic" />
                      : s.server.paused
                        ? <IconBtn icon={Play} label={`Resume ${s.domain}`} onClick={() => toggle(s, 'resume')} />
                        : <IconBtn icon={Pause} label={`Pause ${s.domain}`} onClick={() => toggle(s, 'pause')} />)}
                    <IconBtn icon={Pencil} label={`Edit ${s.domain}`} onClick={() => open(s)} />
                    {managed
                      ? <IconBtn icon={Trash2} danger label={`Delete ${s.domain} from the server`} onClick={() => setDeleting(s)} />
                      : <IconBtn icon={Trash2} danger label={`Remove ${s.domain} from the list`} onClick={() => remove(s)} />}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      )}
      {visitsOf && <VisitsModal domain={visitsOf} onClose={() => setVisitsOf(null)} />}
      {deleting && (
        <DeleteStaticModal site={deleting} onClose={() => { setDeleting(null); load(); }} />
      )}
    </Card>
  );
}

function IconBtn({ icon: Icon, label, onClick, danger = false }) {
  return (
    <button type="button" onClick={onClick} title={label} aria-label={label}
      className={`rounded-ctl p-1 text-ic ${danger ? 'hover:bg-err-bg hover:text-err' : 'hover:bg-s-3 hover:text-t1'}`}>
      <Icon className="w-4 h-4" />
    </button>
  );
}

// Why a row has no Pause / Delete: what vps1 said (or that it couldn't be asked).
function Elsewhere({ server }) {
  const text = server?.managed === null ? 'server unknown'
    : server?.reason === 'no_folder' ? 'no folder set' : 'managed elsewhere';
  return <span className="mr-1.5 text-[12px] text-t3" title={server?.detail || ''}>{text}</span>;
}

/**
 * Delete on the server: type the domain, then a job (like the WordPress
 * delete). The row disappears only once vps1 removed the folder.
 */
function DeleteStaticModal({ site, onClose }) {
  const [typed, setTyped] = useState('');
  const cleanup = useDnsPreview({ path: `/api/sites/static/${encodeURIComponent(site.domain)}/dns-cleanup`, delay: 0 });
  const [removeDns, setRemoveDns] = useState(false);
  const [phase, setPhase] = useState('confirm'); // confirm | running | done | failed
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  async function run() {
    setPhase('running');
    setError(null);
    let jobId;
    try {
      ({ job_id: jobId } = await api(`/api/sites/static/${encodeURIComponent(site.domain)}/server`, {
        method: 'DELETE',
        body: { confirm_domain: typed, ...(removeDns && cleanup.preview?.can_apply ? { dns_cleanup: { fingerprint: cleanup.preview.fingerprint } } : {}) },
      }));
    } catch (err) {
      setError(err.status === 403 ? 'You do not have permission to delete sites.'
        : `${err.message}${err.body?.detail ? ` — ${err.body.detail}` : ''}`);
      setPhase('failed');
      return;
    }
    for (let misses = 0; alive.current;) {
      await new Promise((r) => setTimeout(r, 2000));
      let job;
      try {
        job = await api(`/api/sites/jobs/${jobId}`);
        misses = 0;
      } catch {
        if (++misses >= 8) { setError('No answer from the panel — check Audit before retrying.'); setPhase('failed'); return; }
        continue;
      }
      if (job.status === 'done') {
        setResult(job.result);
        setPhase(job.result?.ok ? 'done' : 'failed');
        return;
      }
      if (job.status === 'failed') {
        setError(`${job.error?.code}${job.error?.detail ? ` — ${job.error.detail}` : ''}`);
        setPhase('failed');
        return;
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-t1/50 p-4">
      <div className="bg-white rounded-xl border border-bd shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 border-b border-bd bg-err-bg px-5 py-3">
          <Trash2 className="w-4 h-4 text-err" />
          <h2 className="text-[14px] font-semibold text-err">Delete {site.domain}</h2>
        </div>
        <div className="p-5 text-[13px] text-t2">
          {phase === 'confirm' && (
            <>
              <p className="mb-2">On the server, in this order (stops at the first failure):</p>
              <ol className="ml-5 mb-4 list-decimal space-y-1">
                <li>Archive <code className="font-mono text-xs">{site.docroot}</code> to <code className="font-mono text-xs">/opt/webance/deleted-sites/</code> (kept)</li>
                <li>Remove its Apache vhosts (checked with configtest first)</li>
                <li>Remove the folder</li>
                <li>Remove its Let's Encrypt certificate — DNS is not touched unless ticked below</li>
              </ol>
              <p className="mb-3">Then the row leaves this list.</p>
              <div className="mb-3 rounded-md border border-bd p-3 space-y-2">
                {cleanup.preview?.can_apply && (
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={removeDns} onChange={(e) => setRemoveDns(e.target.checked)} className="mt-0.5" />
                    <span><span className="font-medium text-t1">Also remove its DNS records</span>
                      <span className="block text-xs text-t3">Only the records below — they point at its server. Removed after the folder is gone.</span></span>
                  </label>
                )}
                <DnsPreview {...cleanup} title="DNS" />
              </div>
              <Input label={`Type ${site.domain} to confirm`} mono value={typed} onChange={(e) => setTyped(e.target.value)} />
            </>
          )}
          {phase === 'running' && (
            <div className="flex items-center gap-3 py-4"><Loader2 className="w-5 h-5 animate-spin" /> Deleting… the archive comes first.</div>
          )}
          {phase === 'done' && (
            <Banner kind="success" title="Deleted">Archive kept on the server: <code className="font-mono text-xs break-all">{result?.steps?.[0]?.path}</code></Banner>
          )}
          {phase === 'failed' && (
            <div className="flex items-start gap-2 text-err">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="min-w-0 break-words">
                <p className="font-semibold">Delete did not finish</p>
                {error && <p className="text-xs">{error}</p>}
                <p className="text-xs mt-1">
                  {result?.panel_row === 'removed'
                    ? 'The folder is gone and the row was removed; a step after that failed — fix it by hand, do not retry.'
                    : 'The row is kept. Steps that ran are listed below.'}
                </p>
              </div>
            </div>
          )}
          {result?.dns && <div className="mt-3"><DnsOutcome dns={result.dns} /></div>}
          {result?.steps?.length > 0 && (
            <ul className="mt-3 space-y-0.5 font-mono text-xs">
              {result.steps.map((st) => (
                <li key={st.step} className={st.ok ? 'text-ok' : st.skipped ? 'text-t3' : 'text-err'}>
                  {st.ok ? '✓' : st.skipped ? '–' : '✗'} {st.step}{st.message ? ` — ${st.message}` : ''}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <Button onClick={onClose} disabled={phase === 'running'}>{phase === 'confirm' ? 'Cancel' : 'Close'}</Button>
            {phase === 'confirm' && (
              <Button variant="danger" icon={Trash2} disabled={typed !== site.domain} onClick={run}>Delete</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  if (!status) return <Empty />;
  return status.up
    ? <Pill state="live">Up{status.code ? ` · ${status.code}` : ''}</Pill>
    : <Pill state="down">Down{status.code ? ` · ${status.code}` : status.error ? ` · ${status.error}` : ''}</Pill>;
}

function Cert({ days }) {
  if (days == null) return <Empty />;
  if (days < 0) return <span className="font-medium text-err">expired {-days} days ago</span>;
  const cls = days < 7 ? 'text-err font-medium' : days < 21 ? 'text-[#8B5206] font-medium' : 'text-t2';
  return <span className={cls}>expires in {days} days</span>;
}
